<template>
    <div v-if="sectionCallouts.length > 0"
        ref="calloutContainer"
        class="page-callout-container w-full grow flex flex-col gap-2"
        data-test="page-callout-container">
        <vx-alert v-for="callout in sectionCallouts"
            :key="callout.id"
            :color="calloutColor(callout.type)"
            :shadow="props.shadow != undefined ? props.shadow : callout.shadow"
            :closeable="props.closeable != undefined ? props.closeable : callout.closeable"
            :calm="props.calm != undefined ? props.calm : callout.calm"
            data-test="page-callout"
            @close="handleClose(callout.id)">
            <template v-slot:icon>
                <font-awesome-icon widthAuto :icon="calloutIcon(callout.type)" />
            </template>
            <div class="flex items-center justify-between gap-4">
                <div v-html="callout.message"></div>
                <vx-button v-if="callout.action"
                    :href="callout.action.href"
                    :color="calloutColor(callout.type)"
                    size="sm"
                    data-test="page-callout-action"
                    @click="handleActionClick($event, callout.action)">
                    {{ callout.action.text || callout.action.label }}
                    <font-awesome-icon widthAuto v-if="callout.action.href || callout.action.onClick"
                        :icon="callout.action.icon || faSquareArrowUpLeft"
                        class="ml-1" />
                </vx-button>
            </div>
        </vx-alert>
    </div>
</template>

<script type="ts" setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { VxAlert, VxButton } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useCallout } from '~/composables/useCallout';
import {
    faCircleCheck,
    faCircleInfo,
    faTriangleExclamation,
    faDiamondExclamation,
    faSquareArrowUpLeft
} from '@fortawesome/pro-solid-svg-icons';

const props = defineProps({
    section: {
        type: String,
        required: true
    },
    // these props are here to override the default behavior from callout.show
    // this facilitates the way the composable is called by having new defaults
    // and not having to pass the same options on several callout.show calls
    closeable: Boolean,
    shadow: Boolean,
    calm: Boolean,
});

const calloutContainer = ref(null);
const callout = useCallout();

const sectionCallouts = computed(() => {
    return callout.forSection(props.section);
});

const calloutColor = (type) => {
    const colorMap = {
        'primary': 'primary',
        'info': 'info',
        'danger': 'danger',
        'success': 'success',
        'violet': 'violet',
        'slate': 'slate',
    };
    return colorMap[type] || 'info';
};

const calloutIcon = (type) => {
    const iconMap = {
        success: faCircleCheck,
        danger: faTriangleExclamation,
        info: faDiamondExclamation
    };
    return iconMap[type] || faCircleInfo;
};

const handleClose = (calloutId) => {
    callout.remove(calloutId);
};

const handleActionClick = (event, action) => {
    if (action.onClick && typeof action.onClick === 'function') {
        event.preventDefault();
        action.onClick();
    } else if (action.scrollTo) {
        event.preventDefault();
        document.querySelector(action.scrollTo)?.scrollIntoView({ behavior: 'smooth' });
    }
};

const scrollToCallout = async () => {
    await nextTick();
    if (calloutContainer.value && sectionCallouts.value.length > 0) {
        const rect = calloutContainer.value.getBoundingClientRect();

        const isInView = (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );

        if (!isInView) {
            calloutContainer.value.scrollIntoView({ behavior: 'smooth' });
        }
    }
};

onMounted(() => {
    if (sectionCallouts.value.length > 0) {
        scrollToCallout();
    }
});
</script>
